import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Editor } from "primereact/editor";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import DetailPopup from "../../../components/DetailPopup/DetailPopup";
import PhotoZoom from "../../../components/PhotoZoom/PhotoZoom";
import { TemaService } from "../../../services/tema.service";
import { Tema } from "../../../services/types";
import { editorHeader, fixQuillLists } from "../../../utils/commonUtil";

const validationSchema = Yup.object().shape({
  baslik: Yup.string()
    .required("Başlık alanı zorunludur")
    .max(35, "Başlık en fazla 35 karakter olmalıdır"),
  aciklama: Yup.string().required("Açıklama alanı zorunludur"),
  sira: Yup.number()
    .required("Sıra alanı zorunludur")
    .min(1, "Sıra 1'den küçük olamaz"),
});

const TemaYonetimi: React.FC = () => {
  const [items, setItems] = useState<Tema[]>([]);
  const [gizliTema, setGizliTema] = useState<Tema | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Tema | null>(null);
  const [loading, setLoading] = useState(false);
  const [photoZoomVisible, setPhotoZoomVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const toast = useRef<Toast>(null);
  const [detailPopupVisible, setDetailPopupVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<Record<string, string>>(
    {}
  );

  const [formData, setFormData] = useState({
    baslik: "",
    aciklama: "",
    sira: 0,
    yatayGorsel: "",
    kapakGorsel: "",
    detayGorsel: "",
    yatayGorselForm: null as File | null,
    kapakGorselForm: null as File | null,
    detayGorselForm: null as File | null,
  });

  const editorSettings = editorHeader();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await TemaService.listele();
      if (response.data) {
        const gizliItem = response.data.find(
          (item) => item.baslik === "gizli" && item.aciklama === "gizli"
        );
        setGizliTema(gizliItem || null);

        const filteredItems = response.data.filter(
          (item) => !(item.baslik === "gizli" && item.aciklama === "gizli")
        );
        setItems(filteredItems);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu",
      });
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      if (editMode && selectedItem) {
        await TemaService.guncelle({
          id: selectedItem.id,
          ...formData,
        });
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Tema başarıyla güncellendi",
        });
      } else {
        if (
          !formData.yatayGorselForm ||
          !formData.kapakGorselForm ||
          !formData.detayGorselForm
        ) {
          toast.current?.show({
            severity: "error",
            summary: "Hata",
            detail: "Lütfen tüm görselleri seçiniz",
          });
          return;
        }
        await TemaService.ekle(formData);
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Tema başarıyla eklendi",
        });
      }
      hideDialog();
      loadData();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Doğrulama Hatası",
            detail: err.message,
          });
        });
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "İşlem sırasında bir hata oluştu",
      });
    }
  };

  const confirmDelete = (item: Tema) => {
    confirmDialog({
      message: "Bu temayı silmek istediğinizden emin misiniz?",
      header: "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: () => handleDelete(item.id),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await TemaService.sil(id);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Tema başarıyla silindi",
      });
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Silme işlemi sırasında bir hata oluştu",
      });
    }
  };

  const showDialog = (item?: Tema) => {
    if (item) {
      setEditMode(true);
      setSelectedItem(item);
      setFormData({
        baslik: item.baslik,
        aciklama: item.aciklama,
        sira: item.sira,
        yatayGorsel: item.yatayGorsel || "",
        kapakGorsel: item.kapakGorsel || "",
        detayGorsel: item.detayGorsel || "",
        yatayGorselForm: null,
        kapakGorselForm: null,
        detayGorselForm: null,
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormData({
        baslik: "",
        aciklama: "",
        sira: 0,
        yatayGorsel: "",
        kapakGorsel: "",
        detayGorsel: "",
        yatayGorselForm: null,
        kapakGorselForm: null,
        detayGorselForm: null,
      });
    }
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditMode(false);
    setSelectedItem(null);
    setFormData({
      baslik: "",
      aciklama: "",
      sira: 0,
      yatayGorsel: "",
      kapakGorsel: "",
      detayGorsel: "",
      yatayGorselForm: null,
      kapakGorselForm: null,
      detayGorselForm: null,
    });
  };

  const actionBodyTemplate = (rowData: Tema) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => showDialog(rowData)}
        />
        <Button
          disabled={rowData.baslik === "gizli" && rowData.aciklama === "gizli"}
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const imageBodyTemplate = (rowData: Tema, field: keyof Tema) => {
    return (
      rowData[field] && (
        <img
          src={rowData[field] as string}
          alt={rowData.baslik}
          className="w-6rem shadow-2 border-round cursor-pointer "
          onClick={() => handleImageClick(rowData[field] as string)}
        />
      )
    );
  };

  const showGizliTemaDialog = () => {
    const gizliItem = gizliTema;

    if (gizliItem) {
      setEditMode(true);
      setSelectedItem(gizliItem);
      setFormData({
        baslik: gizliItem.baslik,
        aciklama: gizliItem.aciklama,
        sira: gizliItem.sira,
        yatayGorsel: gizliItem.yatayGorsel || "",
        kapakGorsel: gizliItem.kapakGorsel || "",
        detayGorsel: gizliItem.detayGorsel || "",
        yatayGorselForm: null,
        kapakGorselForm: null,
        detayGorselForm: null,
      });
      setDialogVisible(true);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setPhotoZoomVisible(true);
  };

  const showDetailPopup = (rowData: Tema) => {
    setSelectedDetail({
      Başlık: rowData.baslik,
      Açıklama: fixQuillLists(rowData.aciklama),
    });
    setDetailPopupVisible(true);
  };

  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-6">
        <div className="flex justify-content-between align-items-center mb-4">
          <h2 className="text-xl">Temalar İlk Görsel</h2>
          <Button
            label="Temalar İlk Görseli Düzenle"
            icon="pi pi-image"
            onClick={showGizliTemaDialog}
          />
        </div>
        {gizliTema?.detayGorsel && (
          <img
            src={gizliTema?.detayGorsel || ""}
            alt="Temalar İlk Görsel"
            className="w-full max-w-20rem shadow-2 border-round cursor-pointer "
            onClick={() => handleImageClick(gizliTema?.detayGorsel || "")}
          />
        )}
      </div>

      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="text-xl">Tema Yönetimi</h1>
        <Button
          label="Yeni Tema Ekle"
          icon="pi pi-plus"
          onClick={() => showDialog()}
        />
      </div>

      <DataTable
        value={items}
        paginator
        rows={10}
        dataKey="id"
        loading={loading}
        emptyMessage="Tema bulunamadı"
        sortField="sira"
        sortOrder={1}
      >
        <Column field="sira" header="Sıra" sortable style={{ width: "10%" }} />
        <Column
          field="baslik"
          header="Başlık"
          sortable
          style={{ width: "15%" }}
        />
        <Column
          field="aciklama"
          header="Açıklama"
          sortable
          style={{ width: "20%" }}
          body={(rowData) => (
            <div
              className="cursor-pointer"
              style={{ whiteSpace: "pre-line" }}
              onClick={() => {
                showDetailPopup(rowData);
              }}
            >
              {rowData.aciklama.length > 100 ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fixQuillLists(rowData.aciklama.substring(0, 100)),
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fixQuillLists(rowData.aciklama),
                  }}
                />
              )}
              <div className="cursor-pointer" style={{ color: "#ed145b" }}>
                Detaya Git &gt;
              </div>
            </div>
          )}
        />
        <Column
          field="yatayGorsel"
          header="Yatay Görsel"
          body={(rowData) => imageBodyTemplate(rowData, "yatayGorsel")}
          style={{ width: "15%" }}
        />
        <Column
          field="kapakGorsel"
          header="Kapak Görsel"
          body={(rowData) => imageBodyTemplate(rowData, "kapakGorsel")}
          style={{ width: "15%" }}
        />
        <Column
          field="detayGorsel"
          header="Detay Görsel"
          body={(rowData) => imageBodyTemplate(rowData, "detayGorsel")}
          style={{ width: "15%" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "10%" }} />
      </DataTable>

      <Dialog
        visible={dialogVisible}
        baseZIndex={100}
        header={editMode ? "Tema Düzenle" : "Yeni Tema Ekle"}
        modal
        className="p-fluid w-full md:w-6"
        onHide={hideDialog}
      >
        <div // burada gizli gizli için kontrol yapıp göstermiyoruz
          style={{
            display:
              editMode &&
              formData.baslik === "gizli" &&
              formData.aciklama === "gizli"
                ? "none"
                : "block",
          }}
        >
          <div className="field mt-4">
            <label htmlFor="baslik">Başlık</label>
            <InputText
              id="baslik"
              value={formData.baslik}
              onChange={(e) =>
                setFormData({ ...formData, baslik: e.target.value })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="aciklama">Açıklama</label>
            <Editor
              id="aciklama"
              value={formData.aciklama}
              headerTemplate={editorSettings}
              onTextChange={(e) =>
                setFormData({ ...formData, aciklama: e.htmlValue || "" })
              }
              style={{ height: "200px" }}
            />
          </div>

          <div className="field">
            <label htmlFor="sira">Sıra</label>
            <InputNumber
              id="sira"
              value={formData.sira}
              onValueChange={(e) =>
                setFormData({ ...formData, sira: e.value || 0 })
              }
            />
          </div>
        </div>

        <div
          className="field"
          style={{
            display:
              editMode &&
              formData.baslik === "gizli" &&
              formData.aciklama === "gizli"
                ? "none"
                : "block",
          }}
        >
          <label htmlFor="yatayGorsel">Yatay Görsel</label>
          {editMode && formData.yatayGorsel && (
            <img
              src={formData.yatayGorsel}
              alt="Yatay Görsel"
              className="w-10rem mb-3 cursor-pointer "
              onClick={() => handleImageClick(formData.yatayGorsel)}
            />
          )}
          <FileUpload
            mode="basic"
            name="yatayGorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                yatayGorselForm: e.files[0],
              })
            }
          />
        </div>

        <div
          className="field"
          style={{
            display:
              editMode &&
              formData.baslik === "gizli" &&
              formData.aciklama === "gizli"
                ? "none"
                : "block",
          }}
        >
          <label htmlFor="kapakGorsel">Kapak Görsel</label>
          {editMode && formData.kapakGorsel && (
            <img
              src={formData.kapakGorsel}
              alt="Kapak Görsel"
              className="w-10rem mb-3 cursor-pointer "
              onClick={() => handleImageClick(formData.kapakGorsel)}
            />
          )}
          <FileUpload
            mode="basic"
            name="kapakGorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                kapakGorselForm: e.files[0],
              })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="detayGorsel">Detay Görsel</label>
          {editMode && formData.detayGorsel && (
            <img
              src={formData.detayGorsel}
              alt="Detay Görsel"
              className="w-10rem mb-3 cursor-pointer "
              onClick={() => handleImageClick(formData.detayGorsel)}
            />
          )}
          <FileUpload
            mode="basic"
            name="detayGorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel={editMode ? "Görseli Değiştir" : "Görsel Seç"}
            onSelect={(e) =>
              setFormData({
                ...formData,
                detayGorselForm: e.files[0],
              })
            }
          />
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            onClick={hideDialog}
            className="mr-2"
          />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} />
        </div>
      </Dialog>

      {photoZoomVisible && selectedImage && (
        <PhotoZoom
          src={selectedImage}
          onClose={() => {
            setPhotoZoomVisible(false);
            setSelectedImage(null);
          }}
        />
      )}

      <DetailPopup
        isOpen={detailPopupVisible}
        onClose={() => setDetailPopupVisible(false)}
        data={selectedDetail}
        header="Tema Detayı"
      />
    </div>
  );
};

export default TemaYonetimi;
